import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../../util/toastHelper.js'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v2'

export const getSubscriptionPlans = createAsyncThunk(
	'subscription/getSubscriptionPlans',
	async (role) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/list?role=${role}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

export const purchaseSubscriptionPlan = createAsyncThunk(
	'subscription/purchaseSubscriptionPlan',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/create`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		}
		catch (err) {
			throw err
		}
	}
)

export const createPaymentSession = createAsyncThunk(
	'subscription/createPaymentSession',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/create-payment-session`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			if (!res.ok || !result?.success)
				throw new Error(
					result.message || 'Failed to create payment session'
				)
			return { checkoutUrl: result.data }
		} catch (err) {
			throw err
		}
	}
)

export const cancelSubscription = createAsyncThunk(
	'subscription/cancelSubscription',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/cancel`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

export const generateInvoice = createAsyncThunk(
	'subscription/generateInvoice',
	async (subscriptionId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/invoice/${subscriptionId}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

export const getPaymentMethods = createAsyncThunk(
	'subscription/getPaymentMethods',
	async (limit = 10) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/payment-methods?limit=${limit}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

export const removePaymentMethod = createAsyncThunk(
	'subscription/removePaymentMethod',
	async (paymentMethodId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/payment-method/${paymentMethodId}`,
				{
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

export const getActiveSubscriptionStatus = createAsyncThunk(
	'subscription/getActiveSubscriptionStatus',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/subscription/status`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			throw err
		}
	}
)

const initialState = {
	plans: [],
	paymentMethods: [],
	subscriptionStatus: null,
	badgeSubscriptionStatus: null,
}
const subscriptionSlice = createSlice({
	name: 'subscription',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(getSubscriptionPlans.fulfilled, (state, { payload }) => {
				if (payload && payload.success) {
					state.plans = payload.data
				} else {
					toastHelper(
						payload.message || 'Failed to fetch subscription plans',
						'error'
					)
				}
			})
			.addCase(cancelSubscription.fulfilled, (state, { payload }) => {
				if (payload && payload.success) {
					toastHelper(
						payload.message ||
							'Subscription cancelled successfully',
						'success'
					)
				} else {
					toastHelper(
						payload.message || 'Failed to cancel subscription',
						'error'
					)
				}
			})
			.addCase(generateInvoice.fulfilled, (state, { payload }) => {
				if (payload && payload.success) {
					toastHelper(
						payload.message || 'Invoice generated successfully',
						'success'
					)
				} else {
					toastHelper(
						payload.message || 'Failed to generate invoice',
						'error'
					)
				}
			})
			.addCase(getPaymentMethods.fulfilled, (state, { payload }) => {
				if (payload && payload.success) {
					state.paymentMethods = payload.data || []
				}
			})
			.addCase(removePaymentMethod.fulfilled, (state, { payload }) => {
				if (payload && payload.success && payload.data) {
					toastHelper(
						payload.message || 'Payment method removed successfully',
						'success'
					)
					state.paymentMethods = state.paymentMethods.filter((pm) => pm.id !== payload.data)
				} else {
					toastHelper(
						payload.message || 'Failed to remove payment method',
						'error'
					)
				}
			})
			.addCase(getActiveSubscriptionStatus.fulfilled, (state, { payload }) => {
				if (payload && payload.success) {
					if (payload.data) {
						state.subscriptionStatus = payload.data
						const badgeTier = Object.keys(payload.data).find(
							(tier) => tier.startsWith('top-')
						)
						if (badgeTier) {
							state.badgeSubscriptionStatus = payload.data[badgeTier]
						} else {
							state.badgeSubscriptionStatus = null
						}
					}
				}
			})
	},
})

export default subscriptionSlice.reducer
